import {
    Box,
    Button,
    Grid,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    VStack,
    Spinner,
    Input,
    Progress,
    Flex,
    Divider,
    useToast,
    Checkbox,
    Badge
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineCloudUpload, AiOutlinePicture, AiOutlineDelete } from "react-icons/ai";
import imageCompression from 'browser-image-compression';
import onGetImageUploadUrl from "../http/uploadImagehandler";
import axios from "axios";

export default function MediaLibraryModal({ isOpen, onClose, onSelectImage }) {
    const { t } = useTranslation('common');
    const toast = useToast();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);

    // Upload States
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatusText, setUploadStatusText] = useState("");

    // Selection State: Set of keys (strings)
    const [selectedKeys, setSelectedKeys] = useState(new Set());

    // Derived state: Last selected image object (for info pane)
    const [lastSelectedKey, setLastSelectedKey] = useState(null);

    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (isOpen) {
            fetchImages();
            setSelectedKeys(new Set());
            setLastSelectedKey(null);
        }
    }, [isOpen]);

    const fetchImages = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/upload/list');
            // Sort by lastModified descending (newest first)
            const sortedImages = (response.data || []).sort((a, b) =>
                new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
            );
            setImages(sortedImages);
            return sortedImages;
        } catch (error) {
            console.error("Failed to load images", error);
            toast({ position: 'top-right', title: 'Error loading library', status: 'error' });
            return [];
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setUploadProgress(10);
        setUploadStatusText("Compressing image...");

        try {
            const options = {
                maxSizeMB: 0.5,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: (p) => setUploadProgress(10 + (p * 0.4))
            };
            const compressedFile = await imageCompression(file, options);

            setUploadStatusText("Uploading to cloud...");
            setUploadProgress(50);

            const uploadUrl = await onGetImageUploadUrl();

            const response = await fetch(uploadUrl.data.preSignedUrl, {
                method: 'PUT',
                body: compressedFile,
            });

            if (!response.ok) throw new Error('Upload failed');

            setUploadProgress(100);
            setUploadStatusText("Upload complete!");

            const refreshedImages = await fetchImages();

            // Auto Select New Image
            const newAccessUrl = uploadUrl.data?.accessUrl;
            if (newAccessUrl) {
                const newImg = refreshedImages.find(img => newAccessUrl.includes(img.key));
                if (newImg) {
                    const newSet = new Set();
                    newSet.add(newImg.key);
                    setSelectedKeys(newSet);
                    setLastSelectedKey(newImg.key);
                }
            }

            setActiveTab(1);
            toast({ position: 'top-right', title: 'Image Uploaded', status: 'success' });

        } catch (error) {
            console.error("Upload error", error);
            toast({ position: 'top-right', title: 'Upload failed', status: 'error' });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Toggle specific key
    const toggleSelection = (key, multi = true) => {
        const newSet = new Set(multi ? selectedKeys : []);
        if (newSet.has(key)) {
            newSet.delete(key);
            // If we deleted the last selected, maybe update lastSelectedKey?
            // If set is empty, lastSelected = null.
            if (key === lastSelectedKey) setLastSelectedKey(null);
        } else {
            newSet.add(key);
            setLastSelectedKey(key);
        }
        setSelectedKeys(newSet);
    };

    const handleImageClick = (key, event) => {
        const isMulti = event.ctrlKey || event.metaKey;
        if (isMulti) {
            toggleSelection(key, true);
        } else {
            // Exclusive select
            const newSet = new Set();
            newSet.add(key);
            setSelectedKeys(newSet);
            setLastSelectedKey(key);
        }
    }

    const handleDelete = async () => {
        if (selectedKeys.size === 0) return;
        if (!window.confirm(t('confirmDelete', `Are you sure you want to permanently delete ${selectedKeys.size} item(s)?`))) return;

        try {
            // Delete all selected
            const deletePromises = Array.from(selectedKeys).map(key =>
                axios.delete(`/upload/${encodeURIComponent(key)}`)
            );

            await Promise.all(deletePromises);

            setImages(images.filter(img => !selectedKeys.has(img.key)));
            setSelectedKeys(new Set());
            setLastSelectedKey(null);

            toast({ position: 'top-right', title: 'Images deleted', status: 'success' });
        } catch (error) {
            console.error(error);
            toast({ position: 'top-right', title: 'Delete failed', status: 'error' });
        }
    }

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    }

    // Get the object for details pane
    const lastSelectedImage = images.find(img => img.key === lastSelectedKey);
    // OR if single selection, find that one
    const singleSelectedImage = selectedKeys.size === 1 ? images.find(img => img.key === Array.from(selectedKeys)[0]) : null;

    const displayImage = singleSelectedImage || lastSelectedImage;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent height="85vh">
                <ModalHeader>{t('mediaLibrary', 'Media Library')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody display="flex" flexDirection="column" p={0}>
                    <Tabs variant="enclosed" index={activeTab} onChange={setActiveTab} flex="1" display="flex" flexDirection="column">
                        <TabList px={4} pt={2}>
                            <Tab><AiOutlineCloudUpload style={{ marginRight: '8px' }} /> {t('upload', 'Upload')}</Tab>
                            <Tab><AiOutlinePicture style={{ marginRight: '8px' }} /> {t('library', 'Library')}</Tab>
                        </TabList>

                        <TabPanels flex="1" overflow="hidden">
                            <TabPanel height="100%">
                                <VStack spacing={8} py={10} borderWidth={2} borderStyle="dashed" borderRadius="md" borderColor="gray.300" height="100%" justifyContent="center">
                                    {uploading ? (
                                        <Box width="80%" textAlign="center">
                                            <Text mb={2} fontWeight="bold">{uploadStatusText}</Text>
                                            <Progress value={uploadProgress} size="lg" colorScheme="blue" hasStripe isAnimated />
                                        </Box>
                                    ) : (
                                        <>
                                            <AiOutlineCloudUpload size={48} color="gray" />
                                            <Text>{t('dragDropOrClick', 'Drag and drop or click to upload')}</Text>
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleUpload}
                                                style={{ display: 'none' }}
                                                id="file-upload"
                                            />
                                            <Button as="label" htmlFor="file-upload" colorScheme="blue" cursor="pointer">
                                                {t('selectFile', 'Select File')}
                                            </Button>
                                            <Text fontSize="sm" color="gray.500">Max size: 500KB (Automatic Compression)</Text>
                                        </>
                                    )}
                                </VStack>
                            </TabPanel>

                            <TabPanel height="100%" p={0}>
                                <Flex height="100%" flexDirection={{ base: 'column', md: 'row' }}>
                                    {/* Grid Area */}
                                    <Box flex="1" overflowY="auto" p={4} bg="white">
                                        <Text fontSize="xs" color="gray.500" mb={4} px={1} borderBottomWidth={1} pb={1}>
                                            {t('multiSelectHint', 'Tip: Hold Cmd/Ctrl to select multiple images, or use the checkmarks.')}
                                        </Text>
                                        {loading ? (
                                            <Spinner />
                                        ) : (
                                            <Grid templateColumns="repeat(auto-fill, minmax(120px, 1fr))" gap={4}>
                                                {images.map((img) => {
                                                    const isSelected = selectedKeys.has(img.key);
                                                    return (
                                                        <Box
                                                            key={img.key}
                                                            onClick={(e) => handleImageClick(img.key, e)}
                                                            cursor="pointer"
                                                            borderWidth={isSelected ? 3 : 1}
                                                            borderColor={isSelected ? "blue.500" : "gray.200"}
                                                            borderRadius="md"
                                                            overflow="hidden"
                                                            aspectRatio={1}
                                                            position="relative"
                                                            _hover={{ borderColor: "blue.300" }}
                                                        >
                                                            <Image
                                                                src={img.url}
                                                                objectFit="cover"
                                                                width="100%"
                                                                height="100%"
                                                                loading="lazy"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    const parent = e.target.parentNode;
                                                                    if (parent) {
                                                                        parent.style.backgroundColor = '#f7fafc';
                                                                        parent.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#a0aec0;font-size:10px;text-align:center;padding:5px;">Not an Image</div>';
                                                                    }
                                                                }}
                                                            />

                                                            <Checkbox
                                                                position="absolute"
                                                                top={2}
                                                                left={2}
                                                                isChecked={isSelected}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    toggleSelection(img.key, true);
                                                                }}
                                                                size="lg"
                                                                colorScheme="blue"
                                                                bg="white"
                                                                borderRadius="sm"
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Grid>
                                        )}
                                    </Box>

                                    {/* Info Sidebar */}
                                    <Box
                                        width={{ base: '100%', md: '300px' }}
                                        borderLeftWidth={{ base: 0, md: 1 }}
                                        borderTopWidth={{ base: 1, md: 0 }}
                                        p={4}
                                        bg="gray.50"
                                        overflowY="auto"
                                        maxHeight={{ base: '40vh', md: 'none' }}
                                    >
                                        {selectedKeys.size > 0 ? (
                                            <VStack align="start" spacing={4}>
                                                <Text fontWeight="bold" fontSize="lg">
                                                    {selectedKeys.size} {t('selected', 'Selected')}
                                                </Text>

                                                {selectedKeys.size === 1 && displayImage && (
                                                    <>
                                                        <Box borderRadius="md" overflow="hidden" borderWidth={1} borderColor="gray.200" width="100%">
                                                            <Image src={displayImage.url} maxH="200px" mx="auto" />
                                                        </Box>

                                                        <VStack align="start" spacing={1} fontSize="sm" width="100%">
                                                            <Text fontWeight="bold">{t('filename', 'Filename')}:</Text>
                                                            <Text noOfLines={2} wordBreak="break-all">{displayImage.key.split('/').pop()}</Text>

                                                            <Divider my={2} />

                                                            <Text fontWeight="bold">{t('uploadDate', 'Uploaded')}:</Text>
                                                            <Text>{new Date(displayImage.lastModified).toLocaleDateString()}</Text>

                                                            <Text fontWeight="bold">{t('size', 'Size')}:</Text>
                                                            <Text>{formatBytes(displayImage.size)}</Text>
                                                        </VStack>
                                                    </>
                                                )}

                                                <Divider />

                                                {/* Select Button: Only if 1 selected */}
                                                {selectedKeys.size === 1 && displayImage && (
                                                    <Button
                                                        colorScheme="blue"
                                                        width="100%"
                                                        onClick={() => {
                                                            onSelectImage(displayImage.url);
                                                            onClose();
                                                        }}
                                                    >
                                                        {t('insertImage', 'Insert Image')}
                                                    </Button>
                                                )}

                                                <Button
                                                    leftIcon={<AiOutlineDelete />}
                                                    colorScheme="red"
                                                    variant="outline"
                                                    size="sm"
                                                    width="100%"
                                                    onClick={handleDelete}
                                                >
                                                    {t('deleteSelected', `Delete (${selectedKeys.size})`)}
                                                </Button>
                                            </VStack>
                                        ) : (
                                            <Flex height="100%" alignItems="center" justifyContent="center" color="gray.500" flexDirection="column" textAlign="center">
                                                <AiOutlinePicture size={48} />
                                                <Text mt={2} fontWeight="bold">{t('selectImage', 'Select an image')}</Text>
                                            </Flex>
                                        )}
                                    </Box>
                                </Flex>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
                <ModalFooter bg="gray.50">
                    <Text fontSize="sm" color="gray.500" mr="auto">
                        {images.length} {t('items', 'items')}
                    </Text>
                    <Button variant="ghost" mr={3} onClick={onClose}>{t('close', 'Close')}</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
